import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminApi, tenantsApi, branchesApi, accountingApi } from '../../api';
import { LoadingState, ErrorState, EmptyState } from '../../components/common';

import { Modal } from '../../components/Modal';

function getErrorMessage(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const NAV = [
  { id: 'overview',     label: 'Overview',           icon: GridIcon },
  { id: 'company',      label: 'Company Profile',    icon: BuildingIcon },
  { id: 'branches',     label: 'Branches',           icon: MapPinIcon },
  { id: 'numbering',    label: 'Document Numbering', icon: HashIcon },
  { id: 'tax',          label: 'Tax & Currency',     icon: PercentIcon },
  { id: 'integrations', label: 'Integrations',       icon: PlugIcon },
  { id: 'security',     label: 'Access & Security',  icon: ShieldIcon },
  { id: 'audit',        label: 'Audit & Compliance', icon: ClipboardIcon },
] as const;

type NavId = typeof NAV[number]['id'];

export function SettingsPage() {
  const [active, setActive] = useState<NavId>('overview');

  return (
    <div className="flex h-full min-h-screen">
      {/* Left settings nav — sits within the page's left margin */}
      <aside className="w-52 flex-shrink-0 border-r border-ink-100 bg-canvas-subtle/40 py-8 px-2">
        <div className="px-2 mb-6">
          <div className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-1">Administration</div>
          <div className="font-display font-bold text-lg text-ink-900 leading-tight">System Settings</div>
        </div>
        <nav className="space-y-0.5">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                active === id
                  ? 'bg-canvas-elevated text-ink-900 shadow-sm ring-1 ring-inset ring-ink-100'
                  : 'text-ink-600 hover:text-ink-900 hover:bg-canvas-elevated'
              }`}
            >
              <Icon size={14} className={`flex-shrink-0 ${active === id ? 'text-ink-700' : 'text-ink-400'}`} />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content panel */}
      <div className="flex-1 min-w-0 px-8 py-10 max-w-4xl">
        {active === 'overview'     && <OverviewSection onNavigate={setActive} />}
        {active === 'company'      && <CompanySection />}
        {active === 'branches'     && <BranchesSection />}
        {active === 'numbering'    && <NumberingSection />}
        {active === 'tax'          && <TaxCurrencySection />}
        {active === 'integrations' && <IntegrationsSection />}
        {active === 'security'     && <SecuritySection />}
        {active === 'audit'        && <AuditSection />}
      </div>
    </div>
  );
}

// ─── Shared UI pieces ─────────────────────────────────────────────────────────
function SectionHead({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-8">
      <h1 className="font-display font-bold text-2xl text-ink-900">{title}</h1>
      <p className="text-sm text-ink-500 mt-1 leading-relaxed max-w-2xl">{subtitle}</p>
    </div>
  );
}

function SettingsCard({
  title, subtitle, children, action,
}: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="card overflow-hidden mb-5">
      <div className="px-5 py-4 border-b border-ink-100 flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-ink-900">{title}</div>
          {subtitle && <div className="text-xs text-ink-500 mt-0.5 leading-relaxed max-w-lg">{subtitle}</div>}
        </div>
        {action}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function HealthTile({
  label, value, status = 'neutral', onClick,
}: { label: string; value: string | number; status?: 'ok'|'warn'|'error'|'neutral'; onClick?: () => void }) {
  const bar   = { ok:'bg-sage-500', warn:'bg-amber-500', error:'bg-danger', neutral:'bg-ink-200' }[status];
  const vCls  = { ok:'text-sage-700', warn:'text-amber-700', error:'text-danger', neutral:'text-ink-900' }[status];
  return (
    <button
      onClick={onClick}
      className={`card p-5 relative overflow-hidden text-left w-full ${onClick ? 'hover:shadow-md transition-shadow cursor-pointer' : 'cursor-default'}`}
    >
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500 text-2xs mb-2">{label}</div>
      <div className={`font-display font-bold text-3xl tabular-nums ${vCls}`}>{value}</div>
    </button>
  );
}

function SaveBar({ dirty, loading, onSave, onDiscard }: {
  dirty: boolean; loading: boolean; onSave: () => void; onDiscard: () => void;
}) {
  if (!dirty) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between gap-4 px-8 py-3 bg-canvas-elevated border-t border-ink-200 shadow-xl">
      <div className="flex items-center gap-2.5 text-sm text-ink-700">
        <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 animate-pulse" />
        Unsaved changes
      </div>
      <div className="flex gap-2">
        <button className="btn-secondary" onClick={onDiscard}>Discard</button>
        <button className="btn-primary" disabled={loading} onClick={onSave}>
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? 'bg-sage-600' : 'bg-ink-200'}`}
      role="switch"
      aria-checked={checked}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

function ToggleRow({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-ink-100 last:border-b-0 gap-4">
      <div>
        <div className="text-sm text-ink-700 font-medium">{label}</div>
        {hint && <div className="text-2xs text-ink-500 mt-0.5 leading-relaxed">{hint}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────
function OverviewSection({ onNavigate }: { onNavigate: (id: NavId) => void }) {
  const tenantQ  = useQuery({ queryKey: ['tenant-current'], queryFn: tenantsApi.getCurrent });
  const seqQ     = useQuery({ queryKey: ['doc-sequences'],  queryFn: adminApi.listDocumentSequences });
  const intQ     = useQuery({ queryKey: ['admin-integ'],    queryFn: adminApi.listIntegrations });
  const branchQ  = useQuery({ queryKey: ['branches'],       queryFn: branchesApi.list });

  const intActive  = (intQ.data  ?? []).filter((i: any) => i.status === 'active').length;
  const intError   = (intQ.data  ?? []).filter((i: any) => i.status === 'error').length;
  const seqCount   = (seqQ.data  ?? []).length;
  const branchCount= (branchQ.data ?? []).length;

  return (
    <>
      <SectionHead
        title="Configuration overview"
        subtitle="Health snapshot of your OmniRetail configuration. Click any tile to navigate to that section."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <HealthTile label="Branches" value={branchCount}
          status={branchCount > 0 ? 'ok' : 'warn'} onClick={() => onNavigate('branches')} />
        <HealthTile label="Doc. sequences" value={seqCount}
          status={seqCount > 0 ? 'ok' : 'warn'} onClick={() => onNavigate('numbering')} />
        <HealthTile label="Active integrations" value={intActive}
          status={intError > 0 ? 'error' : intActive > 0 ? 'ok' : 'neutral'} onClick={() => onNavigate('integrations')} />
        <HealthTile label="Integration errors" value={intError}
          status={intError > 0 ? 'error' : 'ok'} onClick={() => onNavigate('integrations')} />
      </div>

      {tenantQ.isLoading ? <LoadingState rows={2} /> : tenantQ.data && (
        <SettingsCard title="Tenant identity" subtitle="Core company profile from the tenant record.">
          <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {([
              ['Organisation', tenantQ.data.name],
              ['Tenant code',  tenantQ.data.code],
              ['Tax reg.',     tenantQ.data.tax_registration || '—'],
              ['Base currency',tenantQ.data.base_currency],
              ['Fiscal year',  `Starts ${MONTHS[(tenantQ.data.fiscal_year_start_month ?? 1) - 1]}`],
              ['Status',       tenantQ.data.status],
            ] as [string, string][]).map(([label, val]) => (
              <div key={label}>
                <dt className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-0.5">{label}</dt>
                <dd className="font-medium text-ink-900 text-sm">{val}</dd>
              </div>
            ))}
          </dl>
        </SettingsCard>
      )}

      <SettingsCard title="Settings sections" subtitle="All configurable areas of your OmniRetail installation.">
        <div className="grid grid-cols-2 gap-2">
          {NAV.filter(n => n.id !== 'overview').map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => onNavigate(id)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-ink-100 hover:border-sage-300 hover:bg-sage-50/40 text-sm text-ink-700 font-medium transition-all text-left group">
              <div className="w-7 h-7 rounded-lg bg-canvas-subtle flex items-center justify-center flex-shrink-0 group-hover:bg-sage-100 transition-colors">
                <Icon size={13} className="text-ink-500 group-hover:text-sage-700" />
              </div>
              {label}
            </button>
          ))}
        </div>
      </SettingsCard>
    </>
  );
}

// ─── Company ──────────────────────────────────────────────────────────────────
function CompanySection() {
  const qc = useQueryClient();
  const tenantQ   = useQuery({ queryKey: ['tenant-current'],  queryFn: tenantsApi.getCurrent });
  const settingsQ = useQuery({ queryKey: ['admin-settings'],  queryFn: adminApi.getSettings });

  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (settingsQ.data && !dirty) setForm(settingsQ.data ?? {});
  }, [settingsQ.data]);

  const patch = (key: string, val: any) => { setForm(f => ({ ...f, [key]: val })); setDirty(true); };

  const saveMut = useMutation({
    mutationFn: () => adminApi.patchSettings(form),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['admin-settings'] }); setDirty(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <>
      <SectionHead title="Company Profile" subtitle="Your organisation's legal identity, locale, and fiscal configuration." />

      {tenantQ.data && (
        <SettingsCard title="Tenant record" subtitle="Read-only — these fields are set at the database level. Contact your system administrator to change them.">
          <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {([
              ['Organisation name',    tenantQ.data.name],
              ['Tenant code',          tenantQ.data.code],
              ['Tax registration no.', tenantQ.data.tax_registration || '—'],
              ['Base currency',        tenantQ.data.base_currency],
              ['Fiscal year start',    MONTHS[(tenantQ.data.fiscal_year_start_month ?? 1) - 1]],
              ['Status',               tenantQ.data.status],
            ] as [string, string][]).map(([label, val]) => (
              <div key={label}>
                <dt className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-0.5">{label}</dt>
                <dd className="font-medium text-ink-900 text-sm">{val}</dd>
              </div>
            ))}
          </dl>
        </SettingsCard>
      )}

      {settingsQ.isLoading ? <LoadingState rows={3} /> : settingsQ.error ? <ErrorState error={settingsQ.error} /> : (
        <>
          <SettingsCard title="Display & contact" subtitle="Shown on invoices, receipts, and system emails.">
            <div className="grid grid-cols-2 gap-4">
              {([
                { key: 'legal_name',    label: 'Legal name',      placeholder: 'OmniRetail Limited' },
                { key: 'contact_email', label: 'Contact email',   placeholder: 'admin@company.com' },
                { key: 'contact_phone', label: 'Contact phone',   placeholder: '+1 555 000 0000' },
                { key: 'address',       label: 'Address',         placeholder: '123 Main Street, City, Country' },
              ] as { key: string; label: string; placeholder: string }[]).map(({ key, label, placeholder }) => (
                <div key={key} className={key === 'address' ? 'col-span-2' : ''}>
                  <label className="label">{label}</label>
                  <input className="input" value={form[key] ?? ''} onChange={(e) => patch(key, e.target.value)} placeholder={placeholder} />
                </div>
              ))}
            </div>
          </SettingsCard>

          <SettingsCard title="Locale defaults" subtitle="Applied when user preferences are not set.">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Default language</label>
                <select className="input" value={form['default_language'] ?? 'en'} onChange={(e) => patch('default_language', e.target.value)}>
                  {[['en','English'],['fr','French'],['es','Spanish'],['ar','Arabic (RTL)'],['zh','Chinese (Simplified)'],['de','German'],['pt','Portuguese']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Default timezone</label>
                <select className="input" value={form['default_timezone'] ?? 'UTC'} onChange={(e) => patch('default_timezone', e.target.value)}>
                  {['UTC','America/New_York','America/Chicago','America/Los_Angeles','America/Sao_Paulo',
                    'Europe/London','Europe/Paris','Europe/Berlin','Africa/Lagos','Africa/Nairobi',
                    'Asia/Dubai','Asia/Kolkata','Asia/Singapore','Asia/Tokyo','Australia/Sydney'].map(tz => (
                    <option key={tz} value={tz}>{tz.replace('_',' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Date format</label>
                <select className="input" value={form['date_format'] ?? 'DD/MM/YYYY'} onChange={(e) => patch('date_format', e.target.value)}>
                  {['DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD','D MMM YYYY'].map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Number format</label>
                <select className="input" value={form['number_format'] ?? '1,234.56'} onChange={(e) => patch('number_format', e.target.value)}>
                  {[['1,234.56','1,234.56  (EN)'],['1.234,56','1.234,56  (EU)'],['1 234,56','1 234,56  (FR/RU)']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
          </SettingsCard>

          <SaveBar dirty={dirty} loading={saveMut.isPending} onSave={() => saveMut.mutate()} onDiscard={() => { setForm(settingsQ.data ?? {}); setDirty(false); }} />
        </>
      )}
    </>
  );
}

// ─── Branches ────────────────────────────────────────────────────────────────
function BranchesSection() {
  const q = useQuery({ queryKey: ['branches'], queryFn: branchesApi.list });

  return (
    <>
      <SectionHead title="Branches" subtitle="Multi-location configuration. Each branch can carry its own tax registration, warehouse link, and receipt branding." />

      {q.isLoading ? <LoadingState rows={4} /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-hidden mb-5">
          <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
            <div>
              <div className="font-semibold text-ink-900">Branch list</div>
              <div className="text-xs text-ink-500 mt-0.5">
                {q.data?.length ?? 0} location{q.data?.length !== 1 ? 's' : ''} · manage in Administration → Branches
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Tax reg.</th><th>Phone</th><th>Email</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={6} className="text-center text-sm text-ink-400 py-8">No branches configured.</td></tr>
                ) : q.data.map((b: any) => (
                  <tr key={b.id}>
                    <td className="font-mono font-bold text-xs text-ink-900">{b.code}</td>
                    <td className="font-medium text-ink-900">{b.name}</td>
                    <td className="font-mono text-xs text-ink-600">{b.tax_registration || <span className="text-ink-400">—</span>}</td>
                    <td className="text-xs text-ink-600">{b.phone || <span className="text-ink-400">—</span>}</td>
                    <td className="text-xs text-ink-600">{b.email || <span className="text-ink-400">—</span>}</td>
                    <td>
                      <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                        b.status === 'active' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-ink-500 ring-ink-200'
                      }`}>{b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SettingsCard title="Configuration scope" subtitle="Which settings are per-branch vs shared across the whole tenant.">
        <div className="grid grid-cols-2 gap-3">
          {([
            ['Tax registration',   'Per branch',    'Branches may have different VAT/GST numbers.'],
            ['Document numbering', 'Tenant-wide',   'Sequences are shared unless configured otherwise.'],
            ['Receipt branding',   'Per branch',    'Logo, footer, and terms can differ per location.'],
            ['Price lists',        'Per branch',    'Assign a default price list per branch in POS.'],
            ['Currencies',         'Tenant-wide',   'Base currency is defined at the tenant level.'],
            ['Tax rates',          'Tenant-wide',   'Rates in Tax & Currency apply to all branches.'],
          ] as [string, string, string][]).map(([label, scope, note]) => (
            <div key={label} className="bg-canvas-subtle rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-semibold text-ink-900 text-sm">{label}</div>
                <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                  scope === 'Per branch' ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-canvas-elevated text-ink-600 ring-ink-200'
                }`}>{scope}</span>
              </div>
              <div className="text-2xs text-ink-500 leading-relaxed">{note}</div>
            </div>
          ))}
        </div>
      </SettingsCard>
    </>
  );
}

// ─── Document Numbering ───────────────────────────────────────────────────────
function NumberingSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['doc-sequences'], queryFn: adminApi.listDocumentSequences });
  const [editing, setEditing] = useState<any | null>(null);

  const patchMut = useMutation({
    mutationFn: ({ id, payload }: any) => adminApi.patchDocumentSequence(id, payload),
    onSuccess: () => {
      toast.success('Sequence updated');
      qc.invalidateQueries({ queryKey: ['doc-sequences'] });
      setEditing(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const preview = (s: any) =>
    `${s.prefix ?? ''}${String(s.next_value).padStart(s.padding_length, '0')}${s.suffix ?? ''}`;

  const DOC_LABELS: Record<string, string> = {
    invoice:'Sales invoice', purchase_order:'Purchase order', grn:'Goods receipt',
    rtv:'Return to vendor', journal_entry:'Journal entry', receipt:'POS receipt',
    stock_transfer:'Stock transfer', stock_adjustment:'Stock adjustment',
    credit_note:'Credit note', debit_note:'Debit note', rfq:'RFQ',
    requisition:'Requisition', payroll:'Payroll batch', offer:'Offer letter',
    customer_payment: 'Customer payment', vendor_payment: 'Vendor payment',
    stock_count: 'Stock count', quote: 'Sales quote',
  };

  const RESET_LABELS: Record<string, string> = {
    never: 'Never resets', yearly: 'Resets yearly', monthly: 'Resets monthly',
  };

  return (
    <>
      <SectionHead
        title="Document Numbering"
        subtitle="Prefix, suffix, padding, and reset rules for every document type. The preview shows the next number that will be generated."
      />

      {q.isLoading ? <LoadingState rows={6} /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="space-y-3">
          {(q.data ?? []).map((s: any) => (
            <div key={s.doc_type} className="card overflow-hidden">
              <div className="px-5 py-3.5 border-b border-ink-100 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-ink-900 text-sm">
                    {DOC_LABELS[s.doc_type] ?? s.doc_type.replace(/_/g,' ')}
                  </div>
                  <div className="text-2xs text-ink-400 font-mono mt-0.5">{s.doc_type}</div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Live preview */}
                  <div className="flex items-center gap-2 bg-sage-50 border border-sage-200 rounded-xl px-3 py-1.5">
                    <span className="text-2xs text-sage-600 font-semibold uppercase tracking-wider">Next</span>
                    <span className="font-display font-bold text-base text-sage-800 font-mono tabular-nums tracking-tight">
                      {preview(s)}
                    </span>
                  </div>
                  <button className="text-xs font-semibold text-ink-600 hover:text-ink-900 px-3 py-1.5 rounded-lg hover:bg-canvas-subtle transition-colors border border-ink-200"
                    onClick={() => setEditing(s)}>Edit</button>
                </div>
              </div>
              <div className="px-5 py-3 grid grid-cols-4 gap-6 bg-canvas-subtle/40">
                {([
                  ['Prefix',   s.prefix  || '(none)'],
                  ['Suffix',   s.suffix  || '(none)'],
                  ['Padding',  `${s.padding_length} digits`],
                  ['Reset',    RESET_LABELS[s.reset_frequency ?? 'never'] ?? s.reset_frequency],
                ] as [string, string][]).map(([label, val]) => (
                  <div key={label}>
                    <div className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-0.5">{label}</div>
                    <div className="font-mono font-semibold text-xs text-ink-900">{val}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SequenceEditModal
          sequence={editing}
          preview={preview}
          onClose={() => setEditing(null)}
          onSave={(payload) => patchMut.mutate({ id: editing.id, payload })}
          loading={patchMut.isPending}
        />
      )}
    </>
  );
}

function SequenceEditModal({ sequence: s, preview, onClose, onSave, loading }: {
  sequence: any; preview: (s: any) => string;
  onClose: () => void; onSave: (p: any) => void; loading: boolean;
}) {
  const [prefix,    setPrefix]    = useState(s.prefix ?? '');
  const [suffix,    setSuffix]    = useState(s.suffix ?? '');
  const [padding,   setPadding]   = useState(String(s.padding_length));
  const [reset,     setReset]     = useState(s.reset_frequency ?? 'never');

  const livePreview = preview({
    ...s, prefix, suffix, padding_length: parseInt(padding) || s.padding_length,
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Administration · Document Numbering</div>
        <h2 className="text-xl font-bold font-display text-ink-900">
          Edit {s.doc_type.replace(/_/g, ' ')} sequence
        </h2>
        <p className="text-sm text-ink-500 mt-1 font-mono">
          Next number: <span className="font-bold text-ink-900">{s.next_value}</span>
        </p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Prefix</label>
            <input className="input font-mono" value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="e.g. INV-" />
          </div>
          <div>
            <label className="label">Suffix</label>
            <input className="input font-mono" value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              placeholder="e.g. -2026" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Number padding (digits)</label>
            <select className="input" value={padding} onChange={(e) => setPadding(e.target.value)}>
              {[3,4,5,6,7,8].map((n) => (
                <option key={n} value={n}>{n} digits ({String(1).padStart(n,'0')})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Counter reset</label>
            <select className="input" value={reset} onChange={(e) => setReset(e.target.value)}>
              <option value="never">Never</option>
              <option value="yearly">Yearly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        {/* Live preview */}
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 text-center">
          <div className="text-2xs text-sage-600 font-semibold uppercase tracking-wider mb-1">Preview — next document</div>
          <div className="font-display font-bold text-2xl text-sage-800 font-mono tracking-tight">{livePreview}</div>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading}
          onClick={() => onSave({
            prefix: prefix || '',
            suffix: suffix || '',
            padding_length: parseInt(padding) || 6,
            reset_frequency: reset,
          })}>
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Tax & Currency ───────────────────────────────────────────────────────────
// ── Country / currency data ───────────────────────────────────────────────────
const CURRENCY_LOCALES: Array<{ code: string; symbol: string; name: string; locale: string; decimals: number }> = [
  { code: 'USD', symbol: '$',   name: 'US Dollar',         locale: 'en-US',    decimals: 2 },
  { code: 'EUR', symbol: '€',   name: 'Euro',              locale: 'de-DE',    decimals: 2 },
  { code: 'GBP', symbol: '£',   name: 'British Pound',     locale: 'en-GB',    decimals: 2 },
  { code: 'JPY', symbol: '¥',   name: 'Japanese Yen',      locale: 'ja-JP',    decimals: 0 },
  { code: 'CNY', symbol: '¥',   name: 'Chinese Yuan',      locale: 'zh-CN',    decimals: 2 },
  { code: 'INR', symbol: '₹',   name: 'Indian Rupee',      locale: 'en-IN',    decimals: 2 },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar',   locale: 'en-CA',    decimals: 2 },
  { code: 'AUD', symbol: 'A$',  name: 'Australian Dollar', locale: 'en-AU',    decimals: 2 },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham',        locale: 'ar-AE',    decimals: 2 },
  { code: 'SAR', symbol: '﷼',   name: 'Saudi Riyal',       locale: 'ar-SA',    decimals: 2 },
  { code: 'SGD', symbol: 'S$',  name: 'Singapore Dollar',  locale: 'en-SG',    decimals: 2 },
  { code: 'MYR', symbol: 'RM',  name: 'Malaysian Ringgit', locale: 'ms-MY',    decimals: 2 },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar',     locale: 'ar-KW',    decimals: 3 },
  { code: 'BHD', symbol: '.د.ب',name: 'Bahraini Dinar',    locale: 'ar-BH',    decimals: 3 },
  { code: 'NGN', symbol: '₦',   name: 'Nigerian Naira',    locale: 'en-NG',    decimals: 2 },
  { code: 'ZAR', symbol: 'R',   name: 'South African Rand',locale: 'en-ZA',    decimals: 2 },
  { code: 'BRL', symbol: 'R$',  name: 'Brazilian Real',    locale: 'pt-BR',    decimals: 2 },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso',      locale: 'es-MX',    decimals: 2 },
  { code: 'CHF', symbol: 'Fr',  name: 'Swiss Franc',       locale: 'de-CH',    decimals: 2 },
  { code: 'KRW', symbol: '₩',   name: 'South Korean Won',  locale: 'ko-KR',    decimals: 0 },
  { code: 'IDR', symbol: 'Rp',  name: 'Indonesian Rupiah', locale: 'id-ID',    decimals: 0 },
  { code: 'PKR', symbol: '₨',   name: 'Pakistani Rupee',   locale: 'ur-PK',    decimals: 2 },
  { code: 'EGP', symbol: 'E£',  name: 'Egyptian Pound',    locale: 'ar-EG',    decimals: 2 },
  { code: 'TRY', symbol: '₺',   name: 'Turkish Lira',      locale: 'tr-TR',    decimals: 2 },
  { code: 'NOK', symbol: 'kr',  name: 'Norwegian Krone',   locale: 'nb-NO',    decimals: 2 },
  { code: 'SEK', symbol: 'kr',  name: 'Swedish Krona',     locale: 'sv-SE',    decimals: 2 },
  { code: 'DKK', symbol: 'kr',  name: 'Danish Krone',      locale: 'da-DK',    decimals: 2 },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar',locale: 'en-NZ',    decimals: 2 },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar',  locale: 'zh-HK',    decimals: 2 },
  { code: 'PHP', symbol: '₱',   name: 'Philippine Peso',   locale: 'en-PH',    decimals: 2 },
  { code: 'THB', symbol: '฿',   name: 'Thai Baht',         locale: 'th-TH',    decimals: 2 },
  { code: 'VND', symbol: '₫',   name: 'Vietnamese Dong',   locale: 'vi-VN',    decimals: 0 },
  { code: 'GHS', symbol: '₵',   name: 'Ghanaian Cedi',     locale: 'en-GH',    decimals: 2 },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling',   locale: 'sw-KE',    decimals: 2 },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling',locale: 'sw-TZ',    decimals: 2 },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal',      locale: 'ar-QA',    decimals: 2 },
  { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial',       locale: 'ar-OM',    decimals: 3 },
  { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar',   locale: 'ar-JO',    decimals: 3 },
  { code: 'ILS', symbol: '₪',   name: 'Israeli Shekel',    locale: 'he-IL',    decimals: 2 },
];

function TaxCurrencySection() {
  const qc = useQueryClient();
  const taxQ      = useQuery({ queryKey: ['tax-classes'], queryFn: accountingApi.listTaxClasses });
  const fxQ       = useQuery({ queryKey: ['fx-rates'],    queryFn: () => accountingApi.listFxRates({ limit: 20 }) });
  const settingsQ = useQuery({ queryKey: ['admin-settings'], queryFn: adminApi.getSettings });

  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [editingTax, setEditingTax] = useState<any | null>(null);
  const [deletingTax, setDeletingTax] = useState<any | null>(null);

  useEffect(() => {
    if (settingsQ.data && !dirty) setForm(settingsQ.data ?? {});
  }, [settingsQ.data]);

  const patch = (key: string, val: any) => { setForm(f => ({ ...f, [key]: val })); setDirty(true); };

  // Auto-fill symbol+locale+decimals when currency changes
  const handleCurrencyChange = (code: string) => {
    const cur = CURRENCY_LOCALES.find(c => c.code === code);
    patch('default_currency', code);
    if (cur) {
      patch('currency_symbol', cur.symbol);
      patch('currency_locale', cur.locale);
      patch('currency_decimals', String(cur.decimals));
    }
  };

  const saveMut = useMutation({
    mutationFn: () => adminApi.patchSettings(form),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['admin-settings'] }); setDirty(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const createTaxMut = useMutation({
    mutationFn: (p: any) => accountingApi.createTaxClass(p),
    onSuccess: () => { toast.success('Tax class created'); qc.invalidateQueries({ queryKey: ['tax-classes'] }); setShowTaxModal(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateTaxMut = useMutation({
    mutationFn: ({ id, payload }: any) => accountingApi.updateTaxClass(id, payload),
    onSuccess: () => { toast.success('Tax class updated'); qc.invalidateQueries({ queryKey: ['tax-classes'] }); setEditingTax(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteTaxMut = useMutation({
    mutationFn: (id: string) => accountingApi.deleteTaxClass(id),
    onSuccess: () => { toast.success('Tax class deleted'); qc.invalidateQueries({ queryKey: ['tax-classes'] }); setDeletingTax(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const selectedCurrency = CURRENCY_LOCALES.find(c => c.code === (form['default_currency'] ?? 'USD'));
  const previewAmount = selectedCurrency
    ? new Intl.NumberFormat(form['currency_locale'] || selectedCurrency.locale, {
        style: 'currency', currency: selectedCurrency.code,
        minimumFractionDigits: parseInt(form['currency_decimals'] ?? '2'),
      }).format(1234.5)
    : '$1,234.50';

  return (
    <>
      <SectionHead
        title="Tax & Currency"
        subtitle="Tax modes, rates, exchange rates, and rounding — operationally critical to invoice and PO calculation."
      />

      {/* ── Currency settings ──────────────────────────────── */}
      {settingsQ.isLoading ? <LoadingState rows={3} /> : settingsQ.error ? <ErrorState error={settingsQ.error} /> : (
        <>
          <SettingsCard
            title="Currency & locale"
            subtitle="Sets the currency symbol, number formatting, and decimal precision used across all invoices, receipts, and reports."
          >
            {/* Scope banner */}
            <div className="mb-5 p-4 bg-canvas-subtle border border-ink-100 rounded-xl">
              <div className="flex gap-3">
                <div className="text-ink-400 flex-shrink-0 mt-0.5">ℹ</div>
                <div className="text-sm text-ink-700 leading-relaxed">
                  <div className="font-semibold mb-1">What this change affects</div>
                  <ul className="space-y-0.5 text-ink-600">
                    <li>✅ <strong>New invoices, orders, and POS transactions</strong> — will use the new currency immediately after saving.</li>
                    <li>✅ <strong>Display formatting</strong> — all amounts across the UI will render with the new symbol and locale.</li>
                    <li>❌ <strong>Existing posted transactions</strong> — already issued invoices and POs keep their original currency. This is correct ERP behaviour — historical records are immutable.</li>
                    <li>ℹ️ <strong>Multi-currency</strong> — individual invoices can override the currency at creation time. Set FX rates in <strong>Accounting → FX Rates</strong> to support revaluation.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label">Country / currency</label>
                <select className="input" value={form['default_currency'] ?? 'USD'} onChange={(e) => handleCurrencyChange(e.target.value)}>
                  {CURRENCY_LOCALES.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.symbol} — {c.code} · {c.name}
                    </option>
                  ))}
                </select>
                <div className="text-2xs text-ink-500 mt-1">Selecting a currency auto-fills symbol, locale, and decimal places below.</div>
              </div>
              <div>
                <label className="label">Currency symbol</label>
                <input className="input font-mono" value={form['currency_symbol'] ?? (selectedCurrency?.symbol ?? '$')}
                  onChange={(e) => patch('currency_symbol', e.target.value)} />
                <div className="text-2xs text-ink-500 mt-1">Override if you need a custom prefix or suffix.</div>
              </div>
              <div>
                <label className="label">Locale code</label>
                <input className="input font-mono text-xs" value={form['currency_locale'] ?? (selectedCurrency?.locale ?? 'en-US')}
                  onChange={(e) => patch('currency_locale', e.target.value)}
                  placeholder="en-US" />
                <div className="text-2xs text-ink-500 mt-1">BCP-47 locale for number formatting (e.g. <code>ar-AE</code> for right-to-left).</div>
              </div>
              <div>
                <label className="label">Decimal places</label>
                <select className="input" value={form['currency_decimals'] ?? '2'} onChange={(e) => patch('currency_decimals', e.target.value)}>
                  <option value="0">0 — whole numbers (JPY, KRW, VND)</option>
                  <option value="2">2 — cents (USD, EUR, GBP)</option>
                  <option value="3">3 — fils (KWD, BHD, OMR)</option>
                </select>
              </div>
              <div>
                <label className="label">Preview</label>
                <div className="input bg-canvas-subtle text-ink-700 font-mono font-semibold tabular-nums cursor-default select-all">
                  {previewAmount}
                </div>
                <div className="text-2xs text-ink-500 mt-1">How 1234.50 renders with current settings.</div>
              </div>
            </div>
          </SettingsCard>

          {/* ── Tax configuration ───────────────────────────── */}
          <SettingsCard title="Tax configuration" subtitle="Global tax defaults applied when a transaction does not specify otherwise.">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Default tax mode</label>
                <select className="input" value={form['tax_mode'] ?? 'exclusive'} onChange={(e) => patch('tax_mode', e.target.value)}>
                  <option value="exclusive">Exclusive — tax added on top of price</option>
                  <option value="inclusive">Inclusive — tax built into price</option>
                </select>
              </div>
              <div>
                <label className="label">Compound tax</label>
                <select className="input" value={form['compound_tax'] ?? 'no'} onChange={(e) => patch('compound_tax', e.target.value)}>
                  <option value="no">Simple — rates applied independently on base</option>
                  <option value="yes">Compound — each rate stacked on previous</option>
                </select>
              </div>
              <div>
                <label className="label">Rounding rule</label>
                <select className="input" value={form['tax_rounding'] ?? 'half_up'} onChange={(e) => patch('tax_rounding', e.target.value)}>
                  <option value="half_up">Half-up (standard)</option>
                  <option value="half_even">Half-even (banker's rounding)</option>
                  <option value="truncate">Truncate</option>
                </select>
              </div>
            </div>
          </SettingsCard>
        </>
      )}

      {/* ── Tax classes CRUD ────────────────────────────────── */}
      <SettingsCard
        title="Tax classes"
        subtitle="Define the tax rates applied to products, invoices, and purchase orders."
        action={<button className="btn-primary btn-sm" onClick={() => setShowTaxModal(true)}>+ New class</button>}
      >
        {taxQ.isLoading ? <LoadingState rows={3} /> : taxQ.error ? <ErrorState error={taxQ.error} /> : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Type</th>
                  <th className="text-right">Rate</th><th>Jurisdiction</th>
                  <th>Compound</th><th>Inclusive</th>
                  <th className="text-right w-36">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!taxQ.data?.length ? (
                  <tr><td colSpan={8}>
                    <EmptyState message="No tax classes yet." hint="Add your first tax class to start applying rates to transactions."
                      action={<button className="btn-primary btn-sm" onClick={() => setShowTaxModal(true)}>+ New class</button>} />
                  </td></tr>
                ) : taxQ.data.map((t: any) => (
                  <tr key={t.id}>
                    <td className="font-mono font-bold text-xs text-ink-900">{t.code}</td>
                    <td className="font-medium text-ink-900">{t.name}</td>
                    <td className="text-xs text-ink-600 capitalize">{t.tax_type}</td>
                    <td className="text-right font-mono font-bold tabular-nums text-sage-700">
                      {parseFloat(t.rate_percent ?? t.rate ?? '0').toFixed(2)}%
                    </td>
                    <td className="text-xs text-ink-600">{t.jurisdiction || <span className="text-ink-400">—</span>}</td>
                    <td className="text-xs text-center">{t.is_compound ? '✓' : <span className="text-ink-300">—</span>}</td>
                    <td className="text-xs text-center">{t.inclusive ? '✓' : <span className="text-ink-300">—</span>}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                          onClick={() => setEditingTax(t)}>Edit</button>
                        <button className="text-xs font-medium px-2 py-1 rounded text-danger hover:bg-red-50 transition-colors"
                          onClick={() => setDeletingTax(t)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>

      {/* ── Exchange rates — link to module ─────────────────── */}
      <SettingsCard title="Exchange rates" subtitle="Multi-currency support — record rates to enable invoice revaluation and cross-currency reporting.">
        <div className="flex items-start gap-4 p-4 bg-canvas-subtle rounded-xl">
          <div className="w-10 h-10 rounded-xl bg-finance-50 text-finance-600 ring-1 ring-inset ring-finance-200 flex items-center justify-center flex-shrink-0 text-lg">⇄</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-ink-900 mb-1">Managed in Accounting → FX Rates</div>
            <div className="text-sm text-ink-600 leading-relaxed mb-3">
              FX rates are stored with an effective date so historical invoices retain their original rate.
              New rates don't overwrite old ones — the system picks the most recent rate on or before
              each transaction date.
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-ink-600 mb-4">
              <div className="flex items-start gap-1.5"><span className="text-sage-600 font-bold flex-shrink-0">✓</span> Record daily/weekly spot rates</div>
              <div className="flex items-start gap-1.5"><span className="text-sage-600 font-bold flex-shrink-0">✓</span> Full rate history preserved</div>
              <div className="flex items-start gap-1.5"><span className="text-sage-600 font-bold flex-shrink-0">✓</span> Revalue open foreign-currency balances</div>
              <div className="flex items-start gap-1.5"><span className="text-sage-600 font-bold flex-shrink-0">✓</span> Per-invoice currency override</div>
            </div>
            <Link to="/accounting/fx" className="btn-secondary inline-flex items-center gap-2 text-sm !py-2 !px-4">
              Open FX Rates module →
            </Link>
          </div>
        </div>
        {fxQ.data && fxQ.data.length > 0 && (
          <div className="mt-4 text-sm text-ink-600">
            <span className="font-semibold text-ink-900 tabular-nums">{fxQ.data.length}</span> rate{fxQ.data.length !== 1 ? 's' : ''} on file ·
            Most recent:{' '}
            <span className="font-mono text-ink-700">
              {fxQ.data[0]?.from_currency}→{fxQ.data[0]?.to_currency} = {parseFloat(fxQ.data[0]?.rate).toFixed(4)}
            </span>
            {' '}({new Date(fxQ.data[0]?.effective_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })})
          </div>
        )}
      </SettingsCard>

      <SaveBar dirty={dirty} loading={saveMut.isPending} onSave={() => saveMut.mutate()} onDiscard={() => { setForm(settingsQ.data ?? {}); setDirty(false); }} />

      {/* ── Tax class modals ────────────────────────────────── */}
      {(showTaxModal || editingTax) && (
        <TaxClassModal
          initial={editingTax}
          onClose={() => { setShowTaxModal(false); setEditingTax(null); }}
          onSubmit={(p) => editingTax
            ? updateTaxMut.mutate({ id: editingTax.id, payload: p })
            : createTaxMut.mutate(p)
          }
          loading={createTaxMut.isPending || updateTaxMut.isPending}
        />
      )}
      {deletingTax && (
        <Modal
          onClose={() => setDeletingTax(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Tax · Delete class</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete {deletingTax.code}?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              <span className="font-semibold text-ink-900">{deletingTax.name}</span> will be permanently removed.
              This will fail if any products or invoices reference this class.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingTax(null)}>Cancel</button>
            <button className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteTaxMut.isPending}
              onClick={() => deleteTaxMut.mutate(deletingTax.id)}>
              {deleteTaxMut.isPending ? 'Deleting…' : 'Delete class'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function TaxClassModal({ initial, onClose, onSubmit, loading }: {
  initial?: any; onClose: () => void; onSubmit: (p: any) => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    rate_percent: initial?.rate_percent ? parseFloat(initial.rate_percent) : 0,
    tax_type: initial?.tax_type ?? 'sales',
    jurisdiction: initial?.jurisdiction ?? 'default',
    is_compound: initial?.is_compound ?? false,
    inclusive: initial?.inclusive ?? false,
  });
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Accounting · Tax</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit tax class' : 'New tax class'}</h2>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="STD" disabled={isEdit} />
        </div>
        <div>
          <label className="label">Rate %</label>
          <input type="number" min={0} max={100} step={0.001} className="input tabular-nums"
            value={form.rate_percent}
            onChange={(e) => setForm({ ...form, rate_percent: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="col-span-2">
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Standard Rate" />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={form.tax_type}
            onChange={(e) => setForm({ ...form, tax_type: e.target.value })}>
            <option value="sales">Sales tax</option>
            <option value="vat">VAT</option>
            <option value="gst">GST</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="label">Jurisdiction</label>
          <input className="input" value={form.jurisdiction}
            onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
            placeholder="default" />
        </div>
        <div className="col-span-2 flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-ink-700">
            <input type="checkbox" checked={form.is_compound}
              onChange={(e) => setForm({ ...form, is_compound: e.target.checked })} />
            <div>
              <div className="font-medium">Compound</div>
              <div className="text-2xs text-ink-500">Stack on top of previous rates.</div>
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-ink-700">
            <input type="checkbox" checked={form.inclusive}
              onChange={(e) => setForm({ ...form, inclusive: e.target.checked })} />
            <div>
              <div className="font-medium">Inclusive</div>
              <div className="text-2xs text-ink-500">Tax built into the listed price.</div>
            </div>
          </label>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.code || !form.name}
          onClick={() => onSubmit(isEdit
            ? { name: form.name, rate_percent: form.rate_percent, tax_type: form.tax_type, jurisdiction: form.jurisdiction, is_compound: form.is_compound, inclusive: form.inclusive }
            : form)}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create class')}
        </button>
      </div>
    </Modal>
  );
}

// ─── Integrations ─────────────────────────────────────────────────────────────
function IntegrationsSection() {
  const qc = useQueryClient();
  const q  = useQuery({ queryKey: ['admin-integ'], queryFn: adminApi.listIntegrations });

  const testMut = useMutation({
    mutationFn: ({ provider, id }: { provider: string; id: string }) =>
      adminApi.testIntegration(provider, { integrationId: id }),
    onSuccess: (d: any) => {
      toast[d.ok ? 'success' : 'error'](d.ok ? `Connected · ${d.details}` : `Failed · ${d.error}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['admin-integ'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const PMETA: Record<string, { label: string; cat: string; desc: string }> = {
    stripe:   { label:'Stripe',   cat:'Payments',  desc:'Card processing and refunds via Stripe API.' },
    sendgrid: { label:'SendGrid', cat:'Email',     desc:'Transactional and marketing email via SendGrid.' },
    twilio:   { label:'Twilio',   cat:'SMS',       desc:'SMS and WhatsApp messaging via Twilio.' },
    slack:    { label:'Slack',    cat:'Alerts',    desc:'Post alerts and approval requests to Slack.' },
    s3:       { label:'AWS S3',   cat:'Storage',   desc:'Document and media storage on S3.' },
    avalara:  { label:'Avalara',  cat:'Tax API',   desc:'Automated tax calculation via AvaTax.' },
    mapbox:   { label:'Mapbox',   cat:'Mapping',   desc:'Address geocoding and location maps.' },
  };

  const grouped: [string, any[]][] = Object.entries(
    (q.data ?? []).reduce((acc: Record<string, any[]>, i: any) => {
      const cat = PMETA[i.provider]?.cat ?? 'Other';
      (acc[cat] = acc[cat] ?? []).push(i);
      return acc;
    }, {})
  );

  return (
    <>
      <SectionHead
        title="Integrations Hub"
        subtitle="Connected 3rd-party services. Click Test to verify credentials against the live provider API."
      />

      {q.isLoading ? <LoadingState rows={4} /> : q.error ? <ErrorState error={q.error} /> : !q.data?.length ? (
        <div className="card p-10 text-center">
          <div className="text-ink-400 text-sm mb-1.5">No integrations configured.</div>
          <div className="text-xs text-ink-400">Add integrations from Administration → Platform → Integrations.</div>
        </div>
      ) : (
        grouped.map(([category, items]) => (
          <div key={category} className="mb-6">
            <div className="eyebrow text-ink-500 mb-3">{category}</div>
            <div className="space-y-2">
              {items.map((i: any) => {
                const meta = PMETA[i.provider];
                const statusCls =
                  i.status === 'active'  ? 'bg-sage-50 text-sage-700 ring-sage-200' :
                  i.status === 'error'   ? 'bg-red-50 text-red-700 ring-red-200' :
                  i.status === 'testing' ? 'bg-blue-50 text-blue-700 ring-blue-200' :
                                           'bg-canvas-subtle text-ink-500 ring-ink-200';
                return (
                  <div key={i.id} className="card px-5 py-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-canvas-subtle border border-ink-100 flex items-center justify-center flex-shrink-0 font-bold font-mono text-xs text-ink-700">
                      {(meta?.label ?? i.provider).slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-ink-900">{meta?.label ?? i.provider}</span>
                        <span className="font-mono text-xs text-ink-400">{i.name}</span>
                      </div>
                      <div className="text-xs text-ink-500 mt-0.5">{meta?.desc}</div>
                      {i.last_tested_at && (
                        <div className="text-2xs text-ink-400 mt-0.5 font-mono tabular-nums">
                          Last tested {new Date(i.last_tested_at).toLocaleString()}
                          {i.last_test_result && ` · ${i.last_test_result}`}
                          {i.last_test_error  && <span className="text-danger"> · {i.last_test_error}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${statusCls}`}>
                        {i.status}
                      </span>
                      <button
                        className="btn-secondary text-xs py-1 px-3"
                        disabled={testMut.isPending}
                        onClick={() => testMut.mutate({ provider: i.provider, id: i.id })}
                      >
                        Test
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </>
  );
}

// ─── Security ─────────────────────────────────────────────────────────────────
function SecuritySection() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['admin-settings'], queryFn: adminApi.getSettings });
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (settingsQ.data && !dirty) setForm(settingsQ.data ?? {});
  }, [settingsQ.data]);

  const patch = (key: string, val: any) => { setForm(f => ({ ...f, [key]: val })); setDirty(true); };

  const saveMut = useMutation({
    mutationFn: () => adminApi.patchSettings(form),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['admin-settings'] }); setDirty(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (settingsQ.isLoading) return <LoadingState rows={4} />;
  if (settingsQ.error) return <ErrorState error={settingsQ.error} />;

  return (
    <>
      <SectionHead
        title="Access & Security"
        subtitle="Password policy, session management, MFA, login alerts, and device restrictions."
      />

      <SettingsCard title="Authentication">
        <ToggleRow label="Require MFA for all users"         hint="Users must set up an authenticator app before they can sign in." checked={!!form['mfa_required']}      onChange={(v) => patch('mfa_required', v)} />
        <ToggleRow label="Email alerts on login"             hint="Send an email when a user signs in from a new IP or device."     checked={!!form['login_alerts']}      onChange={(v) => patch('login_alerts', v)} />
        <ToggleRow label="Restrict to registered devices"    hint="Only devices that have been pre-approved can sign in."           checked={!!form['device_restriction']} onChange={(v) => patch('device_restriction', v)} />
      </SettingsCard>

      <SettingsCard title="Session & lockout">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Session timeout (minutes)</label>
            <input type="number" min={5} max={1440} className="input tabular-nums"
              value={form['session_timeout_minutes'] ?? 60}
              onChange={(e) => patch('session_timeout_minutes', parseInt(e.target.value) || 60)} />
            <div className="text-2xs text-ink-500 mt-1">Idle sessions expire after this period.</div>
          </div>
          <div>
            <label className="label">Max failed login attempts</label>
            <input type="number" min={3} max={20} className="input tabular-nums"
              value={form['max_login_attempts'] ?? 5}
              onChange={(e) => patch('max_login_attempts', parseInt(e.target.value) || 5)} />
            <div className="text-2xs text-ink-500 mt-1">Account locks after this many consecutive failures.</div>
          </div>
          <div>
            <label className="label">Allowed IP ranges <span className="font-normal text-ink-400 normal-case tracking-normal">(CIDR, optional)</span></label>
            <input className="input font-mono text-xs"
              value={form['allowed_ip_ranges'] ?? ''}
              onChange={(e) => patch('allowed_ip_ranges', e.target.value)}
              placeholder="192.168.1.0/24, 10.0.0.0/8" />
            <div className="text-2xs text-ink-500 mt-1">Leave blank to allow all IPs. Comma-separated.</div>
          </div>
          <div>
            <label className="label">Audit log retention (days)</label>
            <input type="number" min={30} max={3650} className="input tabular-nums"
              value={form['audit_retention_days'] ?? 365}
              onChange={(e) => patch('audit_retention_days', parseInt(e.target.value) || 365)} />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Password policy">
        <div className="mb-4">
          <label className="label">Minimum password length</label>
          <input type="number" min={8} max={32} className="input tabular-nums w-32"
            value={form['password_min_length'] ?? 8}
            onChange={(e) => patch('password_min_length', parseInt(e.target.value) || 8)} />
        </div>
        <ToggleRow label="Require uppercase letter"  checked={!!form['password_require_uppercase']}    onChange={(v) => patch('password_require_uppercase', v)} />
        <ToggleRow label="Require number"            checked={!!form['password_require_number']}       onChange={(v) => patch('password_require_number', v)} />
        <ToggleRow label="Require special character" checked={!!form['password_require_special_char']} onChange={(v) => patch('password_require_special_char', v)} />
      </SettingsCard>

      <SaveBar dirty={dirty} loading={saveMut.isPending} onSave={() => saveMut.mutate()} onDiscard={() => { setForm(settingsQ.data ?? {}); setDirty(false); }} />
    </>
  );
}

// ─── Audit & Compliance ───────────────────────────────────────────────────────
function AuditSection() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['admin-settings'], queryFn: adminApi.getSettings });
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (settingsQ.data && !dirty) setForm(settingsQ.data ?? {});
  }, [settingsQ.data]);

  const patch = (key: string, val: any) => { setForm(f => ({ ...f, [key]: val })); setDirty(true); };

  const saveMut = useMutation({
    mutationFn: () => adminApi.patchSettings(form),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['admin-settings'] }); setDirty(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (settingsQ.isLoading) return <LoadingState rows={4} />;
  if (settingsQ.error) return <ErrorState error={settingsQ.error} />;

  return (
    <>
      <SectionHead
        title="Audit & Compliance"
        subtitle="Logging policy, data retention, sensitive field masking, and document compliance controls."
      />

      <SettingsCard title="Audit logging">
        <ToggleRow label="Enable audit logging"              hint="All create/update/delete actions on critical entities are recorded."                       checked={!!form['audit_logging_enabled']}  onChange={(v) => patch('audit_logging_enabled', v)} />
        <ToggleRow label="Log read access to sensitive data" hint="Adds latency. Recommended for PCI/HIPAA environments only."                               checked={!!form['audit_log_reads']}         onChange={(v) => patch('audit_log_reads', v)} />
        <ToggleRow label="Mask sensitive fields"             hint="Credit card numbers, tax IDs, and passwords are redacted in log entries."                 checked={!!form['sensitive_field_masking']}  onChange={(v) => patch('sensitive_field_masking', v)} />
        <ToggleRow label="Notify admins on policy violation" hint="Send email alerts when users attempt actions that exceed their assigned permissions."     checked={!!form['notify_violations']}       onChange={(v) => patch('notify_violations', v)} />
      </SettingsCard>

      <SettingsCard title="Retention policy">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Audit log retention (days)</label>
            <input type="number" min={30} max={3650} className="input tabular-nums"
              value={form['audit_retention_days'] ?? 365}
              onChange={(e) => patch('audit_retention_days', parseInt(e.target.value) || 365)} />
            <div className="text-2xs text-ink-500 mt-1">Entries older than this are purged automatically.</div>
          </div>
          <div>
            <label className="label">Document retention (years)</label>
            <input type="number" min={1} max={20} className="input tabular-nums"
              value={form['document_retention_years'] ?? 7}
              onChange={(e) => patch('document_retention_years', parseInt(e.target.value) || 7)} />
            <div className="text-2xs text-ink-500 mt-1">Invoices, POs, and payroll. Legal minimum varies by jurisdiction.</div>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Compliance notes" subtitle="Visible to auditors via audit export. Use this to record data-processor agreements, certification scopes, etc.">
        <textarea className="input" rows={5}
          value={form['compliance_notes'] ?? ''}
          onChange={(e) => patch('compliance_notes', e.target.value)}
          placeholder="e.g. This system is compliant with GDPR (EU) 2016/679. Data processor agreements in place with all sub-processors as of 2025-01-01. SOC 2 Type II assessment scheduled for Q3 2026." />
      </SettingsCard>

      <SettingsCard title="Export & audit tools" subtitle="Full audit exports are available from Administration → Platform → GDPR. Per-entity history is in the Audit Log page.">
        <div className="flex items-start gap-4 p-1">
          <div className="w-9 h-9 rounded-xl bg-canvas-subtle flex items-center justify-center flex-shrink-0">
            <ClipboardIcon size={15} className="text-ink-500" />
          </div>
          <div>
            <div className="font-semibold text-ink-900 text-sm mb-1">Audit log & GDPR exports</div>
            <div className="text-xs text-ink-600 leading-relaxed mb-3">
              Full audit log exports, data subject exports, and deletion requests are managed in
              {' '}<span className="font-semibold text-ink-900">Administration → Platform → GDPR</span>.
              Per-entity change history (who changed what, when) is accessible on the
              {' '}<span className="font-semibold text-ink-900">Audit Log</span> page.
            </div>
            <div className="flex gap-2">
              <Link to="/admin/platform" className="btn-secondary text-xs">Open Platform settings</Link>
              <Link to="/admin/audit-log" className="btn-secondary text-xs">Open Audit log</Link>
            </div>
          </div>
        </div>
      </SettingsCard>

      <SaveBar dirty={dirty} loading={saveMut.isPending} onSave={() => saveMut.mutate()} onDiscard={() => { setForm(settingsQ.data ?? {}); setDirty(false); }} />
    </>
  );
}

// ─── Icons (inline SVG) ───────────────────────────────────────────────────────
type IP = { size?: number; className?: string };
const ib = ({ size = 16, className = '' }: IP) => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.75,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, className,
});
function GridIcon(p: IP)      { return <svg {...ib(p)}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function BuildingIcon(p: IP)  { return <svg {...ib(p)}><rect x="3" y="2" width="18" height="20" rx="1"/><path d="M9 22V12h6v10"/></svg>; }
function MapPinIcon(p: IP)    { return <svg {...ib(p)}><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function HashIcon(p: IP)      { return <svg {...ib(p)}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>; }
function PercentIcon(p: IP)   { return <svg {...ib(p)}><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>; }
function PlugIcon(p: IP)      { return <svg {...ib(p)}><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 00-2 2v2a4 4 0 004 4h8a4 4 0 004-4v-2a2 2 0 00-2-2z"/></svg>; }
function ShieldIcon(p: IP)    { return <svg {...ib(p)}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>; }
function ClipboardIcon(p: IP) { return <svg {...ib(p)}><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>; }
